import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function GalleryWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // Upload States - Individual für jedes Bild
    const [uploadingStates, setUploadingStates] = useState({});
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 GalleryWidget initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 GalleryWidget: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 GalleryWidget: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 GalleryWidget REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'masonry-grid': {
                columnCount: 3,
                columnCountMobile: 2,
                gridGap: 16,
                gridGapMobile: 12,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                showTitleText: true,
                showDescriptionText: true
            },
            'wide-grid': {
                columnCount: 4,
                columnCountMobile: 2,
                gridGap: 20,
                gridGapMobile: 16,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                showTitleText: true,
                showDescriptionText: true
            },
            'compact-grid': {
                columnCount: 2,
                columnCountMobile: 1,
                gridGap: 12,
                gridGapMobile: 8,
                textAlignment: 'left',
                textAlignmentMobile: 'center',
                showTitleText: true,
                showDescriptionText: false
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ GalleryWidget layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 GalleryWidget layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Gallery Image Management - DIREKTE WIDGET-UPDATES wie SimplisticServices
    const addGalleryImage = () => {
        const currentImages = widget.galleryImages || [];
        
        // Verwende verschiedene Platzhalter-Bilder aus public/img
        const placeholderImages = [
            '/img/yoga.webp',
            '/img/personal_branding.jpg', 
            '/img/methodology.jpg',
            '/img/holistic_lady.jpg',
            '/img/girlboss.jpg',
            '/img/freedom_girl.jpg'
        ];
        
        const placeholderIndex = currentImages.length % placeholderImages.length;
        
        const newImage = {
            id: `img${Date.now()}`,
            url: placeholderImages[placeholderIndex],
            alt: `Gallery Image ${currentImages.length + 1}`,
            title: `Image ${currentImages.length + 1}`
        };
        
        const updatedImages = [...currentImages, newImage];
        
        // ✅ CRITICAL FIX: Update WIDGET direkt, nicht nur editData
        const updatedWidgetData = { ...widget, galleryImages: updatedImages };
        
        console.log('📡 DIRECT WIDGET UPDATE - Add Gallery Image:', { 
            newImage,
            totalImages: updatedImages.length,
            fullWidgetData: updatedWidgetData
        });
        
        // ✅ BYPASS useWidgetEdit: Direkte Widget-Update um Race Conditions zu vermeiden
        onUpdate(updatedWidgetData);
    };

    const removeGalleryImage = (imageId) => {
        const currentImages = widget.galleryImages || [];
        const updatedImages = currentImages.filter(img => img.id !== imageId);
        
        // ✅ CRITICAL FIX: Update WIDGET direkt, nicht nur editData
        const updatedWidgetData = { ...widget, galleryImages: updatedImages };
        
        console.log('📡 DIRECT WIDGET UPDATE - Remove Gallery Image:', { 
            removedImageId: imageId,
            totalImages: updatedImages.length,
            fullWidgetData: updatedWidgetData
        });
        
        // ✅ BYPASS useWidgetEdit: Direkte Widget-Update
        onUpdate(updatedWidgetData);
    };

    const updateGalleryImage = (imageId, field, value) => {
        console.log('🔧 updateGalleryImage called:', { 
            imageId, 
            field, 
            value, 
            currentImages: (widget.galleryImages || []).length
        });
        
        const currentImages = widget.galleryImages || [];
        const updatedImages = currentImages.map(img => 
            img.id === imageId ? { ...img, [field]: value } : img
        );
        
        // ✅ CRITICAL FIX: Update WIDGET direkt, nicht nur editData
        const updatedWidgetData = { ...widget, galleryImages: updatedImages };
        
        console.log('📡 DIRECT WIDGET UPDATE - Update Gallery Image:', { 
            field, 
            newValue: value, 
            imageId,
            totalImages: updatedImages.length,
            updatedImage: updatedImages.find(img => img.id === imageId),
            fullWidgetData: updatedWidgetData
        });
        
        // ✅ BYPASS useWidgetEdit: Direkte Widget-Update um Race Conditions zu vermeiden
        onUpdate(updatedWidgetData);
        
        console.log('✅ Gallery image update sent directly to widget:', {
            updatedImage: updatedImages.find(img => img.id === imageId),
            totalImages: updatedImages.length,
            field: field,
            value: value
        });
    };

    // ✅ DIREKTER FIREBASE UPLOAD - WIE TESTIMONIALS & SERVICES
    const createGalleryImageUploader = (imageId) => {
        return async (file, fieldName, setPreview, setUploadingState) => {
            if (!file) return;
            
            console.log('📤 Gallery image upload started:', { 
                imageId, 
                fileName: file.name, 
                fileSize: file.size
            });
            
            // Size check
            if (file.size > 2 * 1024 * 1024) {
                alert('File is too big (max. 2MB).');
                setUploadingState(false);
                setUploadingStates(prev => ({ ...prev, [imageId]: false }));
                return;
            }

            setUploadingState(true);
            setUploadingStates(prev => ({ ...prev, [imageId]: true }));
            
            try {
                // ✅ DIREKTER FIREBASE UPLOAD - EXAKT WIE TESTIMONIALS
                const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
                const { getAuth } = await import('firebase/auth');
                
                const storage = getStorage();
                const auth = getAuth();
                const userId = auth.currentUser.uid;
                const galleryFieldName = `gallery_${imageId}`;
                const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${galleryFieldName}_${file.name}`);
                const uploadTask = uploadBytesResumable(imageRef, file);

                uploadTask.on('state_changed', 
                    (snapshot) => {
                        // Upload progress
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`📊 Gallery upload progress: ${progress}%`);
                    }, 
                    (error) => {
                        console.error('❌ Gallery upload error:', error);
                        alert('Upload failed. Please try again.');
                        setUploadingState(false);
                        setUploadingStates(prev => ({ ...prev, [imageId]: false }));
                    }, 
                    () => {
                        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                            console.log('✅ Gallery image uploaded successfully:', {
                                imageId,
                                downloadURL,
                                fileName: file.name
                            });
                            
                            // ✅ KRITISCH: Direkte Widget-Update mit neuer URL
                            updateGalleryImage(imageId, 'url', downloadURL);
                            setPreview(downloadURL);
                            setUploadingState(false);
                            setUploadingStates(prev => ({ ...prev, [imageId]: false }));
                            
                            console.log('🔄 Gallery image state updated, widget should refresh now');
                        }).catch((error) => {
                            console.error('❌ Error getting download URL:', error);
                            alert('Failed to get image URL. Please try again.');
                            setUploadingState(false);
                            setUploadingStates(prev => ({ ...prev, [imageId]: false }));
                        });
                    }
                );
            } catch (error) {
                console.error('❌ Firebase import error:', error);
                alert('Upload failed. Please try again.');
                setUploadingState(false);
                setUploadingStates(prev => ({ ...prev, [imageId]: false }));
            }
        };
    };

    // Text Input Configuration - EXAKT WIE HEROHEADER
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Gallery Title',
            type: 'input',
            placeholder: 'Enter gallery title...',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        {
            fieldName: 'titleHighlightWords',
            label: 'Gallery Title - Highlight Words',
            type: 'input',
            placeholder: 'beautiful, creative (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'small', // Anderer Font-Typ als Main Title
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'descriptionText',
            label: 'Gallery Description',
            type: 'richtext', // ✅ Rich Text Editor mit Boldness-Toggle
            placeholder: 'Enter description text with <strong>bold</strong> formatting...',
            height: '120px',
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect'
        }
    ];

    // Color Configuration - Erweitert für Highlight Words
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#000000',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'titleHighlightColor',
                label: 'Title Highlight',
                defaultValue: '#D4A574',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'descriptionColor',
                label: 'Description',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'borderColor',
                label: 'Image Border',
                defaultValue: '#CCCCCC',
                icon: '/img/element.svg'
            }
        ]
    ];

    // Font Configuration - Erweitert für Highlight Words
    const fontConfig = [
        {
            label: 'Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '36',
            defaultWeight: '700'
        },
        {
            label: 'Title - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '36', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'Description',
            sizeField: 'descriptionSize',
            weightField: 'descriptionWeight',
            defaultSize: '18',
            defaultWeight: '400'
        }
    ];

    // Layout Settings Configuration
    const layoutConfig = [
        {
            fieldName: 'marginTop',
            label: 'Space Top',
            defaultValue: '0',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'marginBottom',
            label: 'Space Bottom',
            defaultValue: '40',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'paddingTop',
            label: 'Padding Top',
            defaultValue: '60',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'paddingBottom',
            label: 'Padding Bottom',
            defaultValue: '60',
            icon: '/img/element.svg'
        }
    ];

    // ✅ ADVANCED SETTINGS CONFIGURATION - Gallery-spezifische Properties
    const advancedConfig = [
        // Grid Settings
        {
            fieldName: 'columnCount',
            label: 'Desktop Columns',
            type: 'slider',
            defaultValue: 3,
            min: 2,
            max: 5,
            step: 1,
            unit: '',
            icon: '/img/grid.svg',
            description: 'Number of columns on desktop'
        },
        {
            fieldName: 'columnCountMobile',
            label: 'Mobile Columns',
            type: 'slider',
            defaultValue: 2,
            min: 1,
            max: 3,
            step: 1,
            unit: '',
            icon: '/img/grid.svg',
            description: 'Number of columns on mobile'
        },
        {
            fieldName: 'gridGap',
            label: 'Grid Gap Desktop',
            type: 'slider',
            defaultValue: 16,
            min: 8,
            max: 40,
            step: 2,
            unit: 'px',
            icon: '/img/spacing.svg',
            description: 'Gap between images on desktop'
        },
        {
            fieldName: 'gridGapMobile',
            label: 'Grid Gap Mobile',
            type: 'slider',
            defaultValue: 12,
            min: 4,
            max: 24,
            step: 2,
            unit: 'px',
            icon: '/img/spacing.svg',
            description: 'Gap between images on mobile'
        },
        
        // Image Settings
        {
            fieldName: 'imageRadius',
            label: 'Image Border Radius',
            type: 'slider',
            defaultValue: 8,
            min: 0,
            max: 20,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg',
            description: 'Border radius for gallery images'
        },
        
        // Interactive Settings
        {
            fieldName: 'enableHover',
            label: 'Enable Hover Effects',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/hover.svg'
        },
        {
            fieldName: 'enableLightbox',
            label: 'Enable Lightbox',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/expand.svg'
        },
        
        // 🎨 TITLE HIGHLIGHT SETTINGS - WIE HEROHEADER
        {
            fieldName: 'titleHighlightWeight',
            label: 'Title Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Title Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Title Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        
        // 🎨 TITLE MAX WIDTH
        {
            fieldName: 'titleMaxWidth',
            label: 'Title Max Width',
            type: 'slider',
            defaultValue: 800,
            min: 300,
            max: 1200,
            step: 20,
            unit: 'px',
            icon: '/img/width.svg',
            description: 'Maximum width of the gallery title'
        },
        // 🎨 DESCRIPTION MAX WIDTH
        {
            fieldName: 'descriptionMaxWidth',
            label: 'Description Max Width',
            type: 'slider',
            defaultValue: 600,
            min: 300,
            max: 1000,
            step: 20,
            unit: 'px',
            icon: '/img/width.svg',
            description: 'Maximum width of the description text'
        },

        // Text Alignment
        {
            fieldName: 'textAlignment',
            label: 'Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/align.svg'
        },
        {
            fieldName: 'textAlignmentMobile',
            label: 'Mobile Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/align.svg'
        },
        
        // Visibility Controls
        {
            fieldName: 'showTitleText',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showDescriptionText',
            label: 'Show Description',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        
        // Spacing Controls
        {
            fieldName: 'titleDescriptionGap',
            label: 'Title-Description Gap',
            type: 'slider',
            defaultValue: 16,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            icon: '/img/spacing.svg'
        },
        {
            fieldName: 'descriptionGalleryGap',
            label: 'Description-Gallery Gap',
            type: 'slider',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 4,
            unit: 'px',
            icon: '/img/spacing.svg'
        },
        
        // ✅ IMAGE BORDER SETTINGS - NEW FEATURE
        {
            fieldName: 'border',
            label: 'Enable Image Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/element.svg',
            description: 'Add border around each gallery image'
        },
        {
            fieldName: 'borderWidth',
            label: 'Image Border Width',
            type: 'slider',
            defaultValue: 2,
            min: 0,
            max: 5,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg',
            description: 'Width of the image border (0 = no border)'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="galleryWidget"
                    selectedLayout={editData.layoutType || 'masonry-grid'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* ✅ TEXT SETTINGS */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                />

                {/* ✅ GALLERY MANAGEMENT SECTION - Styled wie andere Komponenten */}
                <div className="widget-edit-section">
                    {/* Section Title - Styled wie andere Komponenten */}
                    <div className="widget-trenner">
                        <h3>
                            <i className="fas fa-images"></i>
                            Gallery Images ({(widget.galleryImages || []).length})
                        </h3>
                        <hr className="hrrrr" />
                    </div>
                    
                    {/* Add New Image Button - Unter dem Titel */}
                    <div style={{ marginBottom: '20px' }}>
                        <button
                            onClick={addGalleryImage}
                            className="add-bullet-point-btn" // Verwende bestehende CSS-Klasse
                            style={{
                                background: '#5C80BC',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '10px 16px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                justifyContent: 'center'
                            }}
                        >
                            <i className="fas fa-plus"></i>
                            Add Gallery Image
                        </button>
                    </div>

                    {/* Gallery Images mit ImageContainer */}
                    <div className="gallery-images-list">
                        {(widget.galleryImages || []).map((image, index) => (
                            <div key={image.id} style={{ marginBottom: '20px' }}>
                                <ImageContainer
                                    label={`Gallery Image ${index + 1}`}
                                    fieldName={`galleryImage_${image.id}`}
                                    currentImage={image.url}
                                    onUpload={createGalleryImageUploader(image.id)}
                                    isUploading={uploadingStates[image.id] || false}
                                    setIsUploading={(uploading) => setUploadingStates(prev => ({ ...prev, [image.id]: uploading }))}
                                    controls={[]}
                                    editData={editData}
                                    handleInputChange={handleInputChange}
                                    theme="default"
                                    compact={true}
                                />
                                
                                {/* Remove Button - Nur Remove Button, keine Meta-Felder */}
                                <div style={{ 
                                    marginTop: '12px', 
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    <button
                                        onClick={() => removeGalleryImage(image.id)}
                                        style={{
                                            background: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <i className="fas fa-trash"></i>
                                        Remove Image
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Empty State */}
                        {(!widget.galleryImages || widget.galleryImages.length === 0) && (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: '#666',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                backgroundColor: '#f9f9f9'
                            }}>
                                <i className="fas fa-images" style={{ fontSize: '32px', marginBottom: '16px', color: '#ccc' }}></i>
                                <p style={{ margin: 0, fontSize: '16px' }}>
                                    No gallery images yet. Click "Add Image" to get started.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ✅ ANIMATION SETTINGS */}
                <AnimationSettingsSection 
                    editData={editData}
                    onUpdate={onUpdate}
                    showSpeedControls={true}
                    showDelayControls={true}
                    defaultExpanded={false}
                />

                {/* ✅ REVOLUTIONARY LAYOUT SETTINGS */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={[]} // Standard Properties only
                />

                {/* ✅ ADVANCED SETTINGS */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* ✅ MORE SETTINGS (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE SECTION */}
                <div className="save-template-section">
                    <div className="save-template-header-text">
                        <h4 className="save-template-title">Save as Template</h4>
                        <p className="save-template-description">
                            Save your current design as a reusable template
                        </p>
                    </div>
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                    >
                        <i className="fas fa-bookmark"></i>
                        Create Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default GalleryWidgetEdit;
